<script lang="ts">
  // (c) Cognisoft Aps, 2022
  // first page after login - and back to this after results

  // url parameters for testing, - a specific step, level, extension:
  // modak-app.dk/?trin=9&niveau=4&udvidelse=subjekt

  import "../global.css";

  import { auth, db } from "../firebase";
  import { doc, onSnapshot } from "firebase/firestore";
  import type { Unsubscribe } from "firebase/firestore";
  import { onMount, onDestroy } from "svelte";

  import { Level, Pages, LevelExtension, Role, DocType, DaDK } from "../enums";

  import { targetVerbAndObjects } from "../globals/targets";
  import { isLogging } from "../globals/isLogging";

  import { route } from "../stores/route.svelte";
  import { currentSentenceNumberForSteps } from "../stores/targets-step567.svelte";
  import { progress } from "../stores/progress.svelte";
  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";
  import {
    loggedIn,
    pwaEmailForResults,
    pwaNameForResults,
    studentUserData,
    user,
    userEmail,
  } from "../stores/user-store.svelte";
  import { levelExtensionProgress } from "../stores/level-extension-progress.svelte";

  import { icons } from "../utilities/icons";
  import {
    playSoundWithAudioTag,
    playSilentWithAudioTag,
  } from "../utilities/playSound";
  import { Icons } from "../utilities/icons";

  import {
    verbAndObjects,
    introVerbAndObjects,
    basis1VerbAndObjects,
    basis2VerbAndObjects,
  } from "../data/verbAndObjects";
  import { editPWASettings } from "../stores/edit-pwa-settings.svelte";

  import IconButton from "../components/IconButton.svelte";

  import YesNoModal from "../modals/YesNoModal.svelte";

  // PROPS ---------------------------------------------------

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // STATE ---------------------------------------------------

  const State = {
    Initializing: "initializing",
    Ready: "ready",
  };

  type State = (typeof State)[keyof typeof State];

  let currentState = $state(State.Initializing);

  let unsubscribe: Unsubscribe;
  let showYesNoModal = $state(false);
  let startPage: Pages = Pages.Step1;
  let testMessage = $state("");
  let levelText = $state("");
  let levelAndTestMessage = $state("");

  // EFFECTS -------------------------------------------------

  $effect(() => {
    levelAndTestMessage = levelText + testMessage;
  });

  // FUNCTIONS ------------------------------------------------

  // initialize training set of sentences ---
  // find four targets with unique features for:
  //   verb-semantics, object-semantics, stressed vowel and first letter
  //   AND has to have a certain combined length of object words
  //   + frequencies

  const makeTargets = async () => {
    let totalWordLength: number;
    let frequencyLimit: number;
    if (studentUserData.value.currentLevel === Level.First) {
      frequencyLimit = 1000;
    } else {
      frequencyLimit = 0;
    }
    if (studentUserData.value.settings.doIntroNextSession) {
      targetVerbAndObjects.value = introVerbAndObjects;
    } else if (studentUserData.value.settings.doTestNextSession) {
      if (studentUserData.value.currentLevel === Level.First) {
        targetVerbAndObjects.value = basis1VerbAndObjects;
      } else {
        targetVerbAndObjects.value = basis2VerbAndObjects;
      }
    } else {
      let minTotalWordLength: number;
      let maxTotalWordLength: number;
      if (studentUserData.value.currentLevel === Level.First) {
        minTotalWordLength = 0;
        maxTotalWordLength = 18;
      } else {
        minTotalWordLength = 18;
        maxTotalWordLength = 26;
      }
      do {
        targetVerbAndObjects.value = [];
        targetVerbAndObjects.value.push(
          verbAndObjects[Math.floor(Math.random() * verbAndObjects.length)],
        );
        targetVerbAndObjects.value.push(
          verbAndObjects[Math.floor(Math.random() * verbAndObjects.length)],
        );
        targetVerbAndObjects.value.push(
          verbAndObjects[Math.floor(Math.random() * verbAndObjects.length)],
        );
        targetVerbAndObjects.value.push(
          verbAndObjects[Math.floor(Math.random() * verbAndObjects.length)],
        );
        totalWordLength =
          targetVerbAndObjects.value[0].object.length +
          targetVerbAndObjects.value[1].object.length +
          targetVerbAndObjects.value[2].object.length +
          targetVerbAndObjects.value[3].object.length;
      } while (
        totalWordLength < minTotalWordLength ||
        totalWordLength > maxTotalWordLength ||
        targetVerbAndObjects.value[0].object.charAt(0) ===
          targetVerbAndObjects.value[1].object.charAt(0) ||
        targetVerbAndObjects.value[0].object.charAt(0) ===
          targetVerbAndObjects.value[2].object.charAt(0) ||
        targetVerbAndObjects.value[0].object.charAt(0) ===
          targetVerbAndObjects.value[3].object.charAt(0) ||
        targetVerbAndObjects.value[1].object.charAt(0) ===
          targetVerbAndObjects.value[2].object.charAt(0) ||
        targetVerbAndObjects.value[1].object.charAt(0) ===
          targetVerbAndObjects.value[3].object.charAt(0) ||
        targetVerbAndObjects.value[2].object.charAt(0) ===
          targetVerbAndObjects.value[3].object.charAt(0) ||
        targetVerbAndObjects.value[0].stressedVowel ===
          targetVerbAndObjects.value[1].stressedVowel ||
        targetVerbAndObjects.value[0].stressedVowel ===
          targetVerbAndObjects.value[2].stressedVowel ||
        targetVerbAndObjects.value[0].stressedVowel ===
          targetVerbAndObjects.value[3].stressedVowel ||
        targetVerbAndObjects.value[1].stressedVowel ===
          targetVerbAndObjects.value[2].stressedVowel ||
        targetVerbAndObjects.value[1].stressedVowel ===
          targetVerbAndObjects.value[3].stressedVowel ||
        targetVerbAndObjects.value[2].stressedVowel ===
          targetVerbAndObjects.value[3].stressedVowel ||
        targetVerbAndObjects.value[0].verb ===
          targetVerbAndObjects.value[1].verb ||
        targetVerbAndObjects.value[0].verb ===
          targetVerbAndObjects.value[2].verb ||
        targetVerbAndObjects.value[0].verb ===
          targetVerbAndObjects.value[3].verb ||
        targetVerbAndObjects.value[1].verb ===
          targetVerbAndObjects.value[2].verb ||
        targetVerbAndObjects.value[1].verb ===
          targetVerbAndObjects.value[3].verb ||
        targetVerbAndObjects.value[2].verb ===
          targetVerbAndObjects.value[3].verb ||
        targetVerbAndObjects.value[0].frequencyObject < frequencyLimit ||
        targetVerbAndObjects.value[1].frequencyObject < frequencyLimit ||
        targetVerbAndObjects.value[2].frequencyObject < frequencyLimit ||
        targetVerbAndObjects.value[3].frequencyObject < frequencyLimit
      );
    }
  };

  const resetResults = () => {
    // reset results
    result.value.lastStepCompleted = 0;
    result.value.startTime = new Date();
    result.value.pauseElapsed = 0;
    // result.value.responseDetails = [];
    result.value.items = [
      targetVerbAndObjects.value[0].sentence,
      targetVerbAndObjects.value[1].sentence,
      targetVerbAndObjects.value[2].sentence,
      targetVerbAndObjects.value[3].sentence,
    ];
    result.value.comprehensionCorrectFirstTry = 0;
    result.value.comprehensionIndependencyIndex = 0;
    result.value.readingCorrectFirstTry = 0;
    result.value.readingIndependencyIndex = 0;
    result.value.spellingCorrectFirstTry = 0;
    result.value.spellingIndependencyIndex = 0;
    result.value.namingCorrectFirstTry = 0;
    result.value.namingIndependencyIndex = 0;
    result.value.wasInterrupted = false;
  };

  const resetHelpCount = () => {
    helpCount.value.comprehension.item11 = 0;
    helpCount.value.comprehension.item12 = 0;
    helpCount.value.comprehension.item13 = 0;
    helpCount.value.comprehension.item14 = 0;
    helpCount.value.comprehension.item41 = 0;
    helpCount.value.comprehension.item42 = 0;
    helpCount.value.comprehension.item43 = 0;
    helpCount.value.reading.item21 = 0;
    helpCount.value.reading.item22 = 0;
    helpCount.value.reading.item23 = 0;
    helpCount.value.reading.item24 = 0;
    helpCount.value.reading.item31 = 0;
    helpCount.value.reading.item32 = 0;
    helpCount.value.reading.item33 = 0;
    helpCount.value.reading.item34 = 0;
    helpCount.value.spelling.item51 = 0;
    helpCount.value.spelling.item52 = 0;
    helpCount.value.spelling.item53 = 0;
    helpCount.value.spelling.item54 = 0;
    helpCount.value.spelling.item61 = 0;
    helpCount.value.spelling.item62 = 0;
    helpCount.value.spelling.item63 = 0;
    helpCount.value.spelling.item64 = 0;
    helpCount.value.spelling.item71 = 0;
    helpCount.value.spelling.item72 = 0;
    helpCount.value.spelling.item73 = 0;
    helpCount.value.spelling.item74 = 0;
    helpCount.value.naming.item91 = 0;
    helpCount.value.naming.item92 = 0;
    helpCount.value.naming.item93 = 0;
    helpCount.value.naming.item94 = 0;
  };

  // button click event routines ---

  const readyClicked = (): void => {
    // for development, adjust progress if jumping to step
    // 3 tasks on each step except step 4

    playSilentWithAudioTag(audioTag);
    tasksCompleted.value = 0;
    resetResults();
    resetHelpCount();

    currentSentenceNumberForSteps.value = 0; // have to reset for development if jumping pages

    // ONLY FOR TESTING !!!! : **************************************************

    // for development, to jump directly to page *******************************
    // console.log("readyClicked, startPage: ", startPage);
    route.current = startPage;

    // ***************************************************************************
  };

  const doLogOut = () => {
    auth.signOut().then(function () {
      localStorage.setItem("rememberMeOnThisDevice", "no");
      loggedIn.value = false;
      route.current = Pages.Login;
    });
  };

  const logOutClicked = () => {
    showYesNoModal = true;
  };

  const resultsClicked = () => {
    pwaEmailForResults.value = userEmail.value;
    pwaNameForResults.value = user.value.name;
    route.return = route.current;
    route.current = Pages.ResultList;
  };

  const settingsClicked = () => {
    editPWASettings.email = userEmail.value;
    editPWASettings.name = user.value.name;
    editPWASettings.pwaRole = user.value.role;
    editPWASettings.active = user.value.active;
    editPWASettings.settings = studentUserData.value.settings;
    editPWASettings.level = studentUserData.value.currentLevel;
    editPWASettings.completedOnCurrentLevel =
      studentUserData.value.completedOnCurrentLevel;
    route.return = Pages.Ready;
    route.current = Pages.Settings;
  };
  // INITIALIZATION -------------------------------------------------------------------

  // // TESTING THAT ALL IMAGES AND SOUNDS FOR SENTENCES EXISTS =================
  // // ONLY FOR DEBUG; DISABLE FOR PRODUCTION

  // // Tests if images exists:

  // import { Path, Extension } from "../enums";
  // import type { VerbAndObjects } from "../types";

  // const imgIsFound = async (item: string, missing: Array<string>) => {
  //   await fetch(Path.Img + item + Extension.Png).then((response) => {
  //     if (!response.ok) {
  //       missing.push("missing foreground: " + item + Extension.Png);
  //     }
  //   });
  // };

  // const bgImgIsFound = async (item: string, missing: Array<string>) => {
  //   await fetch(Path.ImgBg + item + Extension.ImgBg).then((response) => {
  //     if (!response.ok) {
  //       missing.push("missing background: " + item + Extension.ImgBg);
  //     }
  //   });
  // };

  // const testIfImgExits = async () => {
  //   let missing: Array<string> = [];
  //   for (const task of verbAndObjects) {
  //     await imgIsFound(task.image, missing);
  //     await bgImgIsFound(task.image, missing);
  //   }
  //   for (const item of missing) {
  //     console.error(item);
  //   }
  //   console.error("testIfImgExists completed");
  // };

  // // Test if sounds exists:

  // // import { Path, Extension } from "../types";

  // const sndIsFound = async (
  //   theKey: string,
  //   item: string,
  //   missing: Array<string>,
  // ) => {
  //   await fetch(Path.Snd + item + Extension.Mp3).then((response) => {
  //     try {
  //       if (!response.ok) {
  //         missing.push("missing sound, " + theKey + ": " + item);
  //       }
  //     } catch (error) {
  //       console.error(error, "missing sound: ", item);
  //     }
  //   });
  // };

  // const testIfSoundsExits = async (objectArray: VerbAndObjects) => {
  //   let missing: Array<string> = [];

  //   for (const task of objectArray) {
  //     await sndIsFound("sentence", task.sentence, missing);
  //     await sndIsFound("sentenceFull", task.sentenceFull, missing);
  //     await sndIsFound("sentenceOK", task.sentenceOK, missing);
  //     await sndIsFound("subjectName", task.subjectName, missing);
  //     await sndIsFound("verb", task.verb, missing);
  //     await sndIsFound("object", task.object, missing);
  //     await sndIsFound("namingIntro", task.namingIntro, missing);
  //     await sndIsFound(
  //       "namingSuggestionFalse",
  //       task.namingSuggestionFalse,
  //       missing,
  //     );
  //     await sndIsFound(
  //       "namingVerbSuggestionFalse",
  //       task.namingVerbSuggestionFalse,
  //       missing,
  //     );
  //     await sndIsFound("namingVerbIntro", task.namingVerbIntro, missing);
  //     await sndIsFound(
  //       "namingFullSentencePrompt",
  //       task.namingFullSentencePrompt,
  //       missing,
  //     );
  //     await sndIsFound("namingFullSentence", task.namingFullSentence, missing);
  //     await sndIsFound("namingShortPrompt", task.namingShortPrompt, missing);
  //   }
  //   for (const item of missing) {
  //     console.error(item);
  //   }
  //   console.error("testIfSoundExists completed");
  // };

  // testIfSoundsExits(introVerbAndObjects);
  // testIfSoundsExits(basis1VerbAndObjects);
  // testIfSoundsExits(basis2VerbAndObjects);
  // testIfSoundsExits(verbAndObjects);
  // testIfImgExits();

  // ==========================================================================

  unsubscribe = onSnapshot(
    doc(db, DocType.Users, userEmail.value),
    (doc) => {
      const userData = doc.data();
      if (typeof userData === "undefined") {
        console.error("Ready.svelte - userData undefined");
      } else {
        studentUserData.value.sessionsCompleted = userData.sessionsCompleted;
        studentUserData.value.completedOnCurrentLevel =
          userData.completedOnCurrentLevel;
        studentUserData.value.currentLevel = userData.currentLevel;
        studentUserData.value.settings = userData.settings;
        // new for version 1.15: option to have only one spelling step
        if (!userData.settings.hasOwnProperty("simpleSpelling")) {
          userData.settings.simpleSpelling = false;
        }
        studentUserData.value.settings = studentUserData.value.settings;
      }
    },
    (error) => {
      console.error(
        "Ready, user not found, user uid: ",
        userEmail.value,
        " - error ",
        error.message,
      );
    },
  );

  onMount(() => {
    // for extened levels where steps 8 and 9 are repeated
    levelExtensionProgress.value = LevelExtension.Object;
    // url parameters for testing, mostly for jumping to level 9, and extension levels within level 9
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.has("log")) {
      isLogging.value = true;
      console.log("logging is ON from url parameter");
    }
    if (
      urlParams.has("niveau") ||
      urlParams.has("trin") ||
      urlParams.has("udvidelse")
    ) {
      console.log("startet med debug parametre");
      testMessage = "- Debug!";
      studentUserData.value.settings.doTestNextSession = false;
      studentUserData.value.settings.doIntroNextSession = false;
    }
    let extension = "objekt";
    if (urlParams.has("udvidelse")) {
      extension = urlParams.get("udvidelse") || "object";
      console.log("udvidelse: ", extension);
      switch (extension) {
        case "objekt":
          levelExtensionProgress.value = LevelExtension.Object;
          break;
        case "verbum":
          levelExtensionProgress.value = LevelExtension.Verb;
          break;
        case "subjekt":
          levelExtensionProgress.value = LevelExtension.Subject;
          break;
        default:
          levelExtensionProgress.value = LevelExtension.Object;
          console.error(
            "Ready: error in extension switch-parameter: ",
            extension,
            " - set to LevelExtensionProgres.Oject",
          );
      }
    }
    if (urlParams.has("niveau")) {
      const level = urlParams.get("niveau");
      console.log("niveau: ", level);
      switch (level) {
        case "1":
          studentUserData.value.currentLevel = Level.First;
          break;
        case "2":
          studentUserData.value.currentLevel = Level.Second;
          break;
        case "3":
          studentUserData.value.currentLevel = Level.SecondVerb;
          break;
        case "4":
          studentUserData.value.currentLevel = Level.SecondSubject;
          break;
        default:
          studentUserData.value.currentLevel = Level.First;
          console.error(
            "Fejl i niveau switch parameter: ",
            level,
            " - sat til niveau 1",
          );
      }
    }
    if (urlParams.has("trin")) {
      const step = urlParams.get("trin");
      console.log("step: ", step);
      switch (step) {
        case "1":
          startPage = Pages.Step1;
          console.log("trin 1");
          break;
        case "2":
          startPage = Pages.Step2;
          console.log("trin: 2");
          break;
        case "3":
          startPage = Pages.Step3;
          console.log("trin: 3");
          break;
        case "4":
          startPage = Pages.Step4;
          console.log("trin: 4");
          break;
        case "5":
          startPage = Pages.Step5;
          console.log("trin: 5");
          break;
        case "6":
          startPage = Pages.Step6;
          console.log("trin: 6");
          break;
        case "7":
          startPage = Pages.Step7;
          console.log("trin: 7");
          break;
        case "8":
          startPage = Pages.Step8;
          console.log("trin: 8");
          break;
        case "9":
          startPage = Pages.Step9;
          console.log("trin: 9");
          break;
        default:
          startPage = Pages.Step1;
          console.error("Fejl i trin switch parameter: ", step, " - sat til 1");
      }
    }
    progress.value = 0;
    makeTargets();
    playSoundWithAudioTag(audioTag, DaDK.Ready, null);
    currentState = State.Ready;
  });

  onDestroy(() => {
    unsubscribe();
  });

  // FUNCTIONS -----------------------------------------------

  // has to do new targets if level etc changed i database
  makeTargets();
  // has to do new texts  if level etc changed i database
  let tempLevelText = "";
  if (studentUserData.value.settings.doIntroNextSession) {
    tempLevelText = "Dette er et introduktions-gennemløb.";
  } else {
    if (studentUserData.value.settings.doTestNextSession) {
      tempLevelText = "Basis-test - Niveau ";
    } else {
      tempLevelText = "Niveau ";
    }
    if (studentUserData.value.currentLevel === Level.First) {
      tempLevelText = tempLevelText + "1";
    } else if (studentUserData.value.currentLevel === Level.Second) {
      tempLevelText = tempLevelText + "2";
    } else if (studentUserData.value.currentLevel === Level.SecondVerb) {
      tempLevelText = tempLevelText + "3";
    } else if (studentUserData.value.currentLevel === Level.SecondSubject) {
      tempLevelText = tempLevelText + "4";
    } else {
      tempLevelText = tempLevelText + "*** (intet niveau angivet) ***";
    }
    if (!studentUserData.value.settings.doIntroNextSession) {
      tempLevelText =
        tempLevelText +
        " Gennemførte: " +
        studentUserData.value.completedOnCurrentLevel +
        " ";
    }
  }
  levelText = tempLevelText;
</script>

<!-- HTML ===================================================== -->

<main data-page="ready">
  <div class="question">Er du klar?</div>
  <button
    class="button"
    onclick={readyClicked}
    disabled={currentState !== State.Ready}
  >
    <svg class="icon" viewBox={icons[Icons.ThumbsUp].viewBox}>
      {@html icons[Icons.ThumbsUp].path}
    </svg>
    <div class="caption">JA</div>
  </button>
  <div class="info">
    <p>
      {levelAndTestMessage}
    </p>
  </div>
  <div class="bottom-buttons">
    <span class="left-bottom-btn">
      <IconButton
        onclick={logOutClicked}
        textMediumScaled={true}
        icon={Icons.SignOutAlt}
      >
        {#snippet message()}Log ud{/snippet}
      </IconButton>
    </span>
    {#if user.value.role === Role.Private}
      <IconButton
        onclick={settingsClicked}
        textMediumScaled={true}
        icon={Icons.CogLight}
      >
        {#snippet message()}Indstillinger{/snippet}
      </IconButton>
    {/if}
    <span class="right-bottom-btn">
      <IconButton
        onclick={resultsClicked}
        textMediumScaled={true}
        icon={Icons.FileAltRegular}
      >
        {#snippet message()}Resultater{/snippet}
      </IconButton>
    </span>
  </div>
</main>

{#if showYesNoModal}
  <YesNoModal no={() => (showYesNoModal = false)} yes={doLogOut}
    >{#snippet message()}Vil du logge ud?{/snippet}</YesNoModal
  >
{/if}

<!-- CSS ===================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    padding-bottom: 3vmin;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: calc(var(--vh, 1vh) * 100);
    justify-content: space-around;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .question {
    font-size: 15vmin;
    line-height: 25vmin;
  }
  .info {
    font-size: 3vmin;
    line-height: 10vmin;
    color: hsl(0, 0%, 32%);
    font-style: italic;
  }
  .bottom-buttons {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
  .left-bottom-btn {
    margin-left: 5vmin;
    margin-right: 5vmin;
  }
  .right-bottom-btn {
    margin-left: 5vmin;
    margin-right: 5vmin;
  }
  button {
    flex-grow: 1;
    border: solid 6px;
    border-radius: 12px;
    width: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-around;
    background-color: var(--button-background-color);
    border-color: var(--button-border-color);
  }
  button:disabled {
    border-color: var(--button-border-disabled);
  }
  button:not([disabled]):hover {
    cursor: pointer;
    background-color: var(--button-hover-color);
  }
  .icon {
    height: min(calc(var(--vh, 1vh) * 24), 24vmin);
    width: min(calc(var(--vh, 1vh) * 24), 24vmin);
  }
  .caption {
    font-size: min(7vh, 7vw);
  }
</style>
